/**
 * @since 2026-04-21
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

export const PERIOD_OPTIONS = [
  { id: "all", label: "All" },
  { id: "1y", label: "1Y" },
  { id: "3m", label: "3M" },
  { id: "1m", label: "1M" },
  { id: "ytd", label: "YTD" },
];

export const DEFAULT_COLORS = ["#5470c6", "#91cc75", "#fac858", "#ee6666", "#73c0de", "#9a60b4", "#ea7ccc"];

const AXIS_LINE_COLOR = "rgba(255, 255, 255, 0.3)";
const AXIS_LABEL_COLOR = "rgba(255, 255, 255, 0.7)";
const SPLIT_LINE_COLOR = "rgba(255, 255, 255, 0.1)";

const getPeriodStartDate = (period, lastDate) => {
  switch (period) {
    case "1y": {
      const d = new Date(lastDate);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
    case "3m": {
      const d = new Date(lastDate);
      d.setMonth(d.getMonth() - 3);
      return d;
    }
    case "1m": {
      const d = new Date(lastDate);
      d.setMonth(d.getMonth() - 1);
      return d;
    }
    case "ytd":
      return new Date(lastDate.getFullYear(), 0, 1);
    default:
      return null;
  }
};

const getDateFromEntry = (entry) => {
  if (Array.isArray(entry)) return entry[0];
  if (entry && typeof entry === "object") return entry.date;
  return entry;
};

export const filterByPeriod = (data, period, getDate = getDateFromEntry) => {
  if (!data || data.length === 0 || !period || period === "all") return data;
  const lastDate = new Date(getDate(data[data.length - 1]));
  if (isNaN(lastDate.getTime())) return data;
  const startDate = getPeriodStartDate(period, lastDate);
  if (!startDate) return data;
  return data.filter((d) => {
    const t = new Date(getDate(d));
    return !isNaN(t.getTime()) && t >= startDate;
  });
};

const mergeAxis = (userAxis = {}) => {
  const autoscale = {};
  if (userAxis.type === "value" && userAxis.scale === undefined && userAxis.min === undefined) {
    autoscale.scale = true;
  }
  if (userAxis.type === "log") {
    if (userAxis.min === undefined) autoscale.min = "dataMin";
    if (userAxis.max === undefined) autoscale.max = "dataMax";
  }
  return {
    axisLine: { show: true, lineStyle: { color: AXIS_LINE_COLOR }, ...(userAxis.axisLine || {}) },
    axisTick: { show: false, ...(userAxis.axisTick || {}) },
    axisLabel: { color: AXIS_LABEL_COLOR, ...(userAxis.axisLabel || {}) },
    splitLine: { lineStyle: { color: SPLIT_LINE_COLOR }, ...(userAxis.splitLine || {}) },
    ...autoscale,
    ...userAxis,
  };
};

const themeAxis = (axis) => {
  if (Array.isArray(axis)) return axis.map(mergeAxis);
  return mergeAxis(axis || {});
};

export const applyDarkChartDefaults = (option) => {
  if (!option) return option;
  const themed = {
    color: option.color || DEFAULT_COLORS,
    backgroundColor: "transparent",
    textStyle: { color: AXIS_LABEL_COLOR, ...(option.textStyle || {}) },
    ...option,
    tooltip: {
      backgroundColor: "#191A1D",
      borderColor: "#78BCFB",
      borderWidth: 1,
      textStyle: { color: "rgba(255, 255, 255, 0.85)", fontSize: 12 },
      ...(option.tooltip || {}),
    },
    legend: option.legend
      ? {
          textStyle: { color: "rgba(255, 255, 255, 0.85)", fontSize: 12 },
          ...option.legend,
        }
      : undefined,
  };
  if (option.xAxis) themed.xAxis = themeAxis(option.xAxis);
  if (option.yAxis) themed.yAxis = themeAxis(option.yAxis);
  return themed;
};
